/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import AvatarUploader from '../../components/avatarUploader';
import DatePicker from '../../components/datePicker';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';

const labelClass = 'block text-sm font-medium text-ink-900 mb-1.5';
const inputClass = 'w-full box-border h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500';
const errorClass = 'block text-xs text-[#CC4525] mt-1';

function ChangeDetailsModal({
  formData, address, setFormData, closeModal, profileUpdateRequests, fetchFaceIdData,
}) {
  const { user, setUserAccount } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [curAddress, setCurAddress] = useState({
    location: { description: address.description },
    latlng: { lat: address.latitude, lng: address.longitude },
  });

  const handleFormData = (e) => {
    if ((e.target.name === 'firstName' || e.target.name === 'lastName') && e.target.value.length > 255) {
      displayNotification({
        message: 'The name must be less than 255 characters.',
        severity: 'error',
      });
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveChanges = async () => {
    setFormErrors({});
    setSaving(true);
    const _formData = new FormData();
    Object.keys(formData).forEach((field) => _formData.append(field, formData[field]));

    if (curAddress.isDirty) {
      const response = await sendRequest({
        method: 'post',
        url: API.USER.updateAddressRequest,
        data: curAddress,
      });

      if (response.success) {
        setCurAddress({ ...curAddress, isDirty: false });
        fetchFaceIdData();
      }
    }

    const response = await sendRequest({
      method: 'put',
      url: API.USER.account,
      params: { userId: user.id },
      data: _formData,
    });
    setSaving(false);
    if (response.success) {
      const { avatar, userToken } = response.data;
      setFormData({ ...formData, avatar });
      setUserAccount(userToken);
      displayNotification({
        message: 'The account is updated successfully.',
        severity: 'success',
      });
      closeModal();
    } else {
      setFormErrors(response.data);
    }
  };

  // Only the address change goes through manager approval; name / DOB / avatar
  // are written immediately. Label the button to match what will happen.
  const saveLabel = curAddress.isDirty ? 'Request for Approval' : 'Save Changes';

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="change-details-modal"
    >
      <Box style={{ width: 560, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h5 className="text-lg font-semibold leading-none text-ink-900" data-testid="modal-title">Edit Account</h5>
          <button type="button" aria-label="Close" onClick={closeModal} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-2 pb-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={labelClass}>First Name</span>
              <input
                type="text"
                name="firstName"
                value={formData.firstName || ''}
                onChange={handleFormData}
                placeholder="Required"
                className={inputClass}
                style={{ borderStyle: 'solid', borderColor: formErrors?.firstName ? '#CC4525' : undefined }}
              />
              {formErrors?.firstName && <span className={errorClass}>{formErrors.firstName}</span>}
            </div>
            <div>
              <span className={labelClass}>Last Name</span>
              <input
                type="text"
                name="lastName"
                value={formData.lastName || ''}
                onChange={handleFormData}
                placeholder="Required"
                className={inputClass}
                style={{ borderStyle: 'solid', borderColor: formErrors?.lastName ? '#CC4525' : undefined }}
              />
              {formErrors?.lastName && <span className={errorClass}>{formErrors.lastName}</span>}
            </div>
          </div>

          <div>
            <span className={labelClass}>Email Address</span>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              disabled
              className={`${inputClass} bg-gray-50 text-ink-500 cursor-not-allowed`}
              style={{ borderStyle: 'solid' }}
            />
          </div>

          <div>
            <span className={labelClass}>Date of Birth</span>
            <div className="custom-date-icon">
              <DatePicker
                name="birthday"
                format="MM/dd/yyyy"
                id="date-of-birth"
                oneTap
                value={formData.birthday ? new Date(formData.birthday) : null}
                onChange={(date) => setFormData({ ...formData, birthday: date })}
                placeholder="Required"
                error={formErrors?.birthday ? true : undefined}
              />
            </div>
          </div>

          <div>
            <PlacesAutocomplete
              inlineLabel
              location={curAddress.location}
              onChange={setCurAddress}
              textfieldType="outlined"
              label="Current Address"
              placeholder="Enter Current Address"
            />
          </div>

          {profileUpdateRequests?.address?.new_value?.formattedAddress && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-ink-900">New Requested Address</span>
                <span className="text-xs text-[#B3650F]">Changes will apply after approval.</span>
              </div>
              <input
                type="text"
                value={profileUpdateRequests.address.new_value.formattedAddress}
                disabled
                className={`${inputClass} bg-gray-50 text-ink-500 cursor-not-allowed`}
                style={{ borderStyle: 'solid' }}
              />
            </div>
          )}

          <div>
            <span className={labelClass}>Profile Picture</span>
            <AvatarUploader
              defaultAvatar={formData.avatar}
              onChange={(avatar) => setFormData({ ...formData, avatar })}
              id="profile-picture"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-6 pb-5 pt-1">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSaveChanges}
            className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ChangeDetailsModal.propTypes = {
  formData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    birthday: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    avatar: PropTypes.string,
  }),
  address: PropTypes.shape({
    description: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  profileUpdateRequests: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  fetchFaceIdData: PropTypes.func.isRequired,
};

ChangeDetailsModal.defaultProps = {
  formData: {},
  address: {},
  profileUpdateRequests: null,
};

export default ChangeDetailsModal;
